import lzjs from 'lzjs';
import type { IAtom } from 'mobx';
import { action, computed, createAtom, observable } from 'mobx';
import { createTransformer } from 'mobx-utils';
import { v4 as uuid } from 'uuid';
const { localStorage = { getItem: () => null }, addEventListener = () => null } = window; // hack for SSR

class CachedObjectStore<T> {
  protected atom: IAtom;
  protected key: string;
  public version: string;
  hasRead: boolean = false;

  public getById = createTransformer<number | string, T>((id) => {
    if (!id) {
      return null;
    }
    return this.all.get(id.toString()) || null;
  });

  @observable loaded = false;
  _all = {};

  constructor(key: string) {
    this.atom = createAtom(key);
    this.key = key;
    this.version = localStorage.getItem(`cache:${this.key}:version`) || uuid();
    addEventListener('storage', this.storageInOtherTabChanged);
  }

  @computed
  get All(): T {
    return this.all || {};
  }

  @action.bound
  public onLoad(key: string, cacheResponse: any): void {
    const version = cacheResponse.keys[key];
    const req = cacheResponse[key];

    this.loaded = true;
    if (version === this.version) {
      return;
    }

    if (!req) {
      return;
    }

    if (this.hasRead) {
      this._all = req;
    }
    this.version = version;
    this.saveToLocalStorage(version, req);
    this.atom.reportChanged();
  }

  @action.bound
  public onClear(): void {
    this.loaded = false;
    this._all = {};
    this.hasRead = false;
    this.atom.reportChanged();
    this.onRequestClear();
    this.clearLocalStorage();
  }

  onRequestClear(): void {
    //for override in permissions store
  }

  protected get all(): T {
    if (!this.hasRead) {
      const res = this.loadFromLocalStorage();
      if (!!res) {
        this._all = res;
      }
      this.hasRead = true;
    }

    this.atom.reportObserved();
    return this._all;
  }

  protected saveToLocalStorage(cacheKey: string, obj: T) {
    try {
      localStorage.setItem(`cache:${this.key}`, lzjs.compress(JSON.stringify(obj)));
      // set the version second, so that it won't be there if the storage quota errors
      localStorage.setItem(`cache:${this.key}:version`, cacheKey);
    } catch {
      // revert in case of storage quota exceeded.
      this._all = obj;
    }
  }

  protected loadFromLocalStorage(): T {
    try {
      const res = localStorage.getItem(`cache:${this.key}`);
      const obj = JSON.parse(lzjs.decompress(res));
      return obj as T;
    } catch (err) {
      return {} as T;
    }
  }

  protected clearLocalStorage() {
    localStorage.removeItem(`cache:${this.key}`);
    localStorage.removeItem(`cache:${this.key}:version`);
    this.version = uuid();
  }

  @action.bound
  private storageInOtherTabChanged(e: StorageEvent) {
    if (e.key === `cache:${this.key}:version`) {
      this.version = e.newValue;
    }
    if (e.key === `cache:${this.key}`) {
      if (e.newValue) {
        // in case of manual clearing...
        this._all = JSON.parse(lzjs.decompress(e.newValue)) as T;
      }
      this.atom.reportChanged();
    }
  }
}

export default CachedObjectStore;
